import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { StandardURL } from "../App";

const SpecificRental = () => {
  const [rental, setRental] = useState({});
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [showAddPhotoModal, setShowAddPhotoModal] = useState(false);
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Re-introduced loading state
  const [error, setError] = useState(null);     // Re-introduced error state

  useEffect(() => {
    const rentalId = localStorage.getItem("selectedRentalId");
    const token = localStorage.getItem("token");

    if (rentalId && token) {
      setToken(token);

      // Fetch rental data
      axios
        .get(
          `${StandardURL}/iNyumba/admin/rentals/specific/${rentalId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setRental(response.data.response);
        })
        .catch((error) => {
          console.error("Error fetching rental:", error);
        });

      // Fetch photos
      axios
        .get(`${StandardURL}/iNyumba/photos/fetch/${rentalId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          setPhotos(response.data.response);
        })
        .catch((error) => {
          console.error("Error fetching photos:", error);
        });

      // Fetch videos
      axios
        .get(`${StandardURL}/iNyumba/videos/fetch/${rentalId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          setVideos(response.data.response);
        })
        .catch((error) => {
          console.error("Error fetching videos:", error);
        });
    }
    setLoading(false);
  }, []);

  const handleAddPhoto = () => {
    setShowAddPhotoModal(true);
  };

  const handleAddVideo = () => {
    setShowAddVideoModal(true);
  };

  const closeModal = () => {
    setShowAddPhotoModal(false);
    setShowAddVideoModal(false);
  };

  const handlePhotoFileChange = (e) => {
    setPhotoFile(e.target.files[0]);
  };

  const handleVideoFileChange = (e) => {
    setVideoFile(e.target.files[0]);
  };

  const addPhoto = () => {
    const formData = new FormData();
    formData.append("image", photoFile);

    axios
      .post(
        `${StandardURL}/iNyumba/images/images/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success("Photo uploaded successfully!");
        setLoading(false); // Set loading to false on success
        // Update photos state with the response URL
        const newPhoto = { photo_url: response.data.response.coverUri };
        setPhotos([...photos, newPhoto]);
        // After receiving the response, add the photo to the rental
        addPhotoToRental(newPhoto);
        closeModal();
      })
      .catch((error) => {
        setLoading(false); // Set loading to false on success
        toast.error(error.response.data.response);
        console.error("Error uploading photo:", error);
      });
  };

  const addPhotoToRental = (photo) => {
    const rentalId = localStorage.getItem("selectedRentalId");
    axios
      .post(
        `${StandardURL}/iNyumba/photos/add/${rentalId}`,
        {
          photo_url: photo.photo_url,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
      })
      .catch((error) => {
        toast.error(error.response.data.response);
        console.error("Error adding photo to rental:", error);
      });
  };

  const handleDeletePhoto = (photoId) => {
    localStorage.setItem("deletePhotoId", photoId);
    navigate("/landlord/deletephoto");
  };

  const handleDeleteVideo = (videoId) => {
    localStorage.setItem("deleteVideoId", videoId);
    navigate("/landlord/deletevideo");
  };

  const addVideo = () => {
    const formData = new FormData();
    formData.append("video", videoFile);

    axios
      .post(
        `${StandardURL}/iNyumba/images/images/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success("Video uploaded successfully!");
        // Update videos state with the response URL
        const newVideo = { video_url: response.data.response.coverUri };

        setVideos([...videos, newVideo]);
        addVideoToRental(newVideo);
        setLoading(false); // Set loading to false on success
        closeModal();
      })
      .catch((error) => {
        toast.error(error.response.data.response);
        console.error("Error uploading video:", error);
        setLoading(false); // Set loading to false on success
      });
  };

  const addVideoToRental = (video) => {
    const rentalId = localStorage.getItem("selectedRentalId");
    axios
      .post(
        `${StandardURL}/iNyumba/videos/add/${rentalId}`,
        {
          video_url: video.video_url,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
      })
      .catch((error) => {
        toast.error(error.response.data.response);
        console.error("Error adding video to rental:", error);
      });
  };


  // --- Loader and Error State Rendering ---
  if (loading) {
    return (
      <RoleGuard>
        <AuthGuard>
          <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-500"></div>
              <p className="ml-4 text-xl font-semibold text-gray-700">Loading rental details... Please wait. 🚀</p>
            </div>
          </div>
        </AuthGuard>
      </RoleGuard>
    );
  }

  if (error) {
    return (
      <RoleGuard>
        <AuthGuard>
          <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg shadow-md text-center" role="alert">
              <strong className="font-bold text-lg">Error!</strong>
              <span className="block sm:inline mt-2 text-base">{error}</span>
              <button
                onClick={() => navigate(-1)}
                className="mt-6 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Go Back
              </button>
            </div>
          </div>
        </AuthGuard>
      </RoleGuard>
    );
  }

  // Handle case where no rental data is found after loading (e.g., ID invalid)
  if (!rental) {
    return (
      <RoleGuard>
        <AuthGuard>
          <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-6 py-4 rounded-lg shadow-md text-center" role="alert">
              <strong className="font-bold text-lg">Heads up!</strong>
              <span className="block sm:inline mt-2 text-base"> No rental data found for this ID.</span>
              <button
                onClick={() => navigate(-1)}
                className="mt-6 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-6 rounded-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Go Back
              </button>
            </div>
          </div>
        </AuthGuard>
      </RoleGuard>
    );
  }

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 flex flex-col items-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl w-full bg-white p-8 rounded-lg shadow-xl">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-6 text-center">
              Specific Rental Details 🏡
            </h1>
            <hr className="my-6 border-gray-200" />

            {/* Display rental details */}
            <div className="mb-8 p-6 bg-blue-50 rounded-lg shadow-sm border border-blue-200">
              <h2 className="text-2xl font-bold text-blue-800 mb-4">Rental Information</h2>
              <p className="text-lg text-gray-700 mb-2">
                <strong className="font-semibold text-gray-800">Description:</strong>{" "}
                {rental.description}
              </p>
              <p className="text-lg text-gray-700">
                <strong className="font-semibold text-gray-800">House Number:</strong>{" "}
                {rental.house_number}
              </p>
            </div>

            {/* Buttons for adding photos/videos */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <button
                onClick={handleAddPhoto}
                className="flex items-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300 ease-in-out"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-4 4 4 4-4V5h-2a1 1 0 110-2h2a3 3 0 013 3v10a3 3 0 01-3 3H4a3 3 0 01-3-3V5a3 3 0 013-3h2a1 1 0 010 2h-2v10z"
                    clipRule="evenodd"
                  />
                </svg>
                Add Photo
              </button>

              <button
                onClick={handleAddVideo}
                className="flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition duration-300 ease-in-out"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.555 4.168a1 1 0 00-.894 0L10 6.696V14a2 2 0 002 2h4a2 2 0 002-2V6.696L15.394 4.168a1 1 0 00-.839-.089z" />
                </svg>
                Add Video
              </button>
            </div>

            {/* Modal for adding photos */}
            <Modal
              isOpen={showAddPhotoModal}
              onRequestClose={closeModal}
              className="fixed inset-0 flex items-center justify-center p-4 z-50"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-40"
            >
              <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md mx-auto">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Add Photo 📸</h2>
                <div className="mb-6">
                  <label
                    htmlFor="photoFile"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Select Photo File:
                  </label>
                  <input
                    type="file"
                    id="photoFile"
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    onChange={handlePhotoFileChange}
                    accept="image/*" // Suggestion: Restrict to image files
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={addPhoto}
                    className="flex-1 sm:flex-none px-5 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300"
                  >
                    Upload Photo
                  </button>
                  <button
                    onClick={closeModal}
                    className="flex-1 sm:flex-none px-5 py-2 bg-gray-300 text-gray-800 font-semibold rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Modal>

            {/* Modal for adding videos */}
            <Modal
              isOpen={showAddVideoModal}
              onRequestClose={closeModal}
              className="fixed inset-0 flex items-center justify-center p-4 z-50"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-40"
            >
              <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md mx-auto">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Add Video 🎥</h2>
                <div className="mb-6">
                  <label
                    htmlFor="videoFile"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Select Video File:
                  </label>
                  <input
                    type="file"
                    id="videoFile"
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    onChange={handleVideoFileChange}
                    accept="video/*" // Suggestion: Restrict to video files
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={addVideo}
                    className="flex-1 sm:flex-none px-5 py-2 bg-purple-600 text-white font-semibold rounded-md shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition duration-300"
                  >
                    Upload Video
                  </button>
                  <button
                    onClick={closeModal}
                    className="flex-1 sm:flex-none px-5 py-2 bg-gray-300 text-gray-800 font-semibold rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Modal>

            {/* Display photos */}
            <div className="mt-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Photos 🖼️</h2>
              {photos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {photos.map((photo, index) => (
                    <div
                      key={photo.photo_id || index} // Use unique ID if available, fallback to index
                      className="relative bg-gray-100 rounded-lg shadow-md overflow-hidden group hover:shadow-lg transition-shadow duration-300"
                    >
                      <img
                        src={photo.photo_url}
                        alt={`rental photo ${index + 1}`}
                        className="w-full h-48 object-cover cursor-pointer transform group-hover:scale-105 transition-transform duration-300"
                        onClick={() => window.open(photo.photo_url, "_blank")} // Open image in new tab
                      />
                      {/* Delete button positioned with absolute, visible on hover */}
                      <button
                        onClick={() => handleDeletePhoto(photo.photo_id)}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        title="Delete Photo"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm1 3a1 1 0 100 2h4a1 1 0 100-2H8z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-center py-4">No photos available for this rental.</p>
              )}
            </div>

            {/* Display videos */}
            <div className="mt-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Videos 📹</h2>
              {videos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video, index) => (
                    <div
                      key={video.video_id || index} // Use unique ID if available, fallback to index
                      className="relative bg-gray-100 rounded-lg shadow-md overflow-hidden group hover:shadow-lg transition-shadow duration-300"
                    >
                      {/* Aspect ratio for responsive video embeds */}
                      <div className="relative pt-[56.25%]"> {/* 16:9 aspect ratio (9 / 16 * 100 = 56.25%) */}
                        <iframe
                          title={`rental video ${index + 1}`}
                          src={video.video_url}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute top-0 left-0 w-full h-full object-cover rounded-md"
                        ></iframe>
                      </div>
                      {/* Delete button positioned with absolute, visible on hover */}
                      <button
                        onClick={() => handleDeleteVideo(video.video_id)}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        title="Delete Video"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm1 3a1 1 0 100 2h4a1 1 0 100-2H8z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-center py-4">No videos available for this rental.</p>
              )}
            </div>
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default SpecificRental;
